import { captureRemixErrorBoundaryError, withSentry } from '@sentry/remix'
import type {
  MetaFunction,
  LinksFunction,
  LoaderFunctionArgs,
  TypedResponse,
} from '@remix-run/node'
import type { Theme } from '#app/utils/hooks/use-theme'
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  data,
  useRouteError,
} from '@remix-run/react'
import { useEffect } from 'react'
import { useChangeLanguage } from 'remix-i18next/react'
import { AuthenticityTokenProvider } from 'remix-utils/csrf/react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { authenticator } from '#app/modules/auth/auth.server'
import { identify, reset } from '#app/utils/analytics'
import { useNonce } from '#app/utils/hooks/use-nonce'
import { getHints } from '#app/utils/hooks/use-hints'
import { prisma } from '#app/utils/db.server'
import { getTheme, useTheme } from '#app/utils/hooks/use-theme'
import { getToastSession } from '#app/utils/toast.server'
import { csrf } from '#app/utils/csrf.server'
import { honeypot } from '#app/utils/honeypot.server'
import { combineHeaders, getDomainUrl } from '#app/utils/misc.server'
import { siteConfig } from '#app/utils/constants/brand'
import { useToast } from '#app/components/toaster'
import { Toaster } from '#app/components/ui/sonner'
import { ClientHintCheck } from '#app/components/misc/client-hints'
import { GenericErrorBoundary } from '#app/components/misc/error-boundary'
import i18nServer, { localeCookie } from '#app/modules/i18n/i18n.server'

import RootCSS from './root.css?url'

export const handle = { i18n: ['translation'] }

const NO_CANONICAL_PREFIXES = ['/auth', '/dashboard', '/admin', '/onboarding', '/resources']

// TODO(seo): fill in real social profile URLs as we set them up.
const ORG_SAME_AS: string[] = []

const ORGANIZATION_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteConfig.siteTitle,
  url: siteConfig.siteUrl,
  logo: siteConfig.siteImageUrl,
  email: siteConfig.email,
  description: siteConfig.siteDescription,
  sameAs: ORG_SAME_AS,
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const canonicalPath = data?.requestInfo.path ?? '/'
  const isPrivate = NO_CANONICAL_PREFIXES.some((p) => canonicalPath.startsWith(p))
  const canonicalHref = `${siteConfig.siteUrl}${canonicalPath === '/' ? '' : canonicalPath}`
  return [
    { title: data ? `${siteConfig.siteTitle}` : `Error | ${siteConfig.siteTitle}` },
    {
      name: 'description',
      content: siteConfig.siteDescription,
    },
    ...(isPrivate
      ? [{ name: 'robots', content: 'noindex, nofollow' }]
      : [{ tagName: 'link', rel: 'canonical', href: canonicalHref }]),
  ]
}


export const links: LinksFunction = () => {
  return [{ rel: 'stylesheet', href: RootCSS }]
}

export type LoaderData = Exclude<
  Awaited<ReturnType<typeof loader>>,
  Response | TypedResponse<unknown>
>

export async function loader({ request }: LoaderFunctionArgs) {
  const sessionUser = await authenticator.isAuthenticated(request)
  const user = sessionUser?.id
    ? await prisma.user.findUnique({
        where: { id: sessionUser?.id },
        include: {
          image: { select: { id: true } },
          roles: { select: { name: true } },
        },
      })
    : null

  const locale = await i18nServer.getLocale(request)
  const { toast, headers: toastHeaders } = await getToastSession(request)
  const [csrfToken, csrfCookieHeader] = await csrf.commitToken()

  const pathname = new URL(request.url).pathname
  const isAppRoute =
    pathname.startsWith('/dashboard') || pathname.startsWith('/admin')
  const theme: Theme | null = isAppRoute ? getTheme(request) : 'light'

  return data(
    {
      user,
      locale,
      toast,
      csrfToken,
      honeypotProps: honeypot.getInputProps(),
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: pathname,
        userPrefs: { theme },
      },
    } as const,
    {
      headers: combineHeaders(
        { 'Set-Cookie': await localeCookie.serialize(locale) },
        toastHeaders,
        csrfCookieHeader ? { 'Set-Cookie': csrfCookieHeader } : null,
      ),
    },
  )
}

function Document({
  children,
  nonce,
  lang = 'en',
  dir = 'ltr',
  theme = 'light',
}: {
  children: React.ReactNode
  nonce: string
  lang?: string
  dir?: 'ltr' | 'rtl'
  theme?: Theme
}) {
  return (
    <html
      lang={lang}
      dir={dir}
      className={`${theme} overflow-x-hidden`}
      style={{ colorScheme: theme }}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />
        <link
          rel="preconnect"
          href="https://www.google-analytics.com"
          crossOrigin="anonymous"
        />
        <script
          async
          nonce={nonce}
          src="https://www.googletagmanager.com/gtag/js?id=G-M5NFX689M9"
        />
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-M5NFX689M9');
            `,
          }}
        />
        <ClientHintCheck nonce={nonce} />
        <link rel="icon" href="/favicon.ico" sizes="any" />
        <Meta />
        <Links />
        <script
          type="application/ld+json"
          nonce={nonce}
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ORGANIZATION_JSON_LD) }}
        />
      </head>
      <body className="h-auto w-full">
        {children}
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
        <Toaster closeButton position="bottom-center" theme={theme} />
      </body>
    </html>
  )
}

function AppWithProviders() {
  const { user, locale, toast, csrfToken, honeypotProps } =
    useLoaderData<typeof loader>()

  const nonce = useNonce()
  const theme = useTheme()

  // Updates the i18n instance language.
  useChangeLanguage(locale)

  // Renders toast (if any).
  useToast(toast)

  useEffect(() => {
    if (user?.id) {
      identify(user.id, {
        $email: user.email,
        $name: user.username ?? undefined,
        roles: user.roles.map((r) => r.name),
      })
    } else {
      reset()
    }
  }, [user?.id])

  return (
    <Document nonce={nonce} theme={theme} lang={locale ?? 'en'}>
      <AuthenticityTokenProvider token={csrfToken}>
        <HoneypotProvider {...honeypotProps}>
          <Outlet />
        </HoneypotProvider>
      </AuthenticityTokenProvider>
    </Document>
  )
}

export default withSentry(AppWithProviders)

export function ErrorBoundary() {
  const error = useRouteError()
  const nonce = useNonce()
  const theme = useTheme()

  captureRemixErrorBoundaryError(error)

  return (
    <Document nonce={nonce} theme={theme}>
      <GenericErrorBoundary
        statusHandlers={{
          403: ({ error }) => (
            <p>You are not allowed to do that: {error?.data.message}</p>
          ),
        }}
      />
    </Document>
  )
}
